import NiceModal from '@ebay/nice-modal-react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import {
  Alert,
  Button,
  Checkbox,
  Container,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { ErrorReasons } from '@shared/constants/errors.constants'
import {
  CreateCompanyUserBody,
  createCompanyUserBodySchema
} from '@shared/dto/companies.dto'
import { CompanyUsersRelation } from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { LoaderButton } from '../../components/LoaderButton'
import { ResendInvitationModal } from '../../components/modals/ResendInvitationModal'
import SubHeaderProfile from '../../components/SubHeaderProfile'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useCreateCompanyUserMutation } from '../../hooks/useMutations/useCompaniesMutations'
import {
  useCompany,
  useCompanyRoles,
  useCompanyUser
} from '../../hooks/useQueries/useCompanies'
import { useWorkspaces } from '../../hooks/useQueries/useWorkspaces'
import { useSnackbar } from '../../hooks/useSnackbar'
import { isDefaultRole } from '../../utils/user.utils'

const paperStyles = {
  padding: '1.5rem',
  borderRadius: '.25rem',
  background: '#232629'
}

export default function CompaniesCreateInvitation() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const companyId = params.companyId as string
  const { userId } = useAuthStore()
  const { data: user, isLoading: isCompanyUserLoading } = useCompanyUser(
    companyId,
    userId!
  )
  const { data: company, isLoading: isCompanyLoading } = useCompany(companyId)
  const { data: roles, isLoading: isRolesLoading } = useCompanyRoles(companyId)
  const { data: workspaces, isLoading: isWorkspacesLoading } =
    useWorkspaces(companyId)
  const { showSnackbar } = useSnackbar()
  const createCompanyUserMutation = useCreateCompanyUserMutation(companyId)

  const canCreateMembers = useMemo(() => {
    return (
      user?.permissions?.includes(Permission.USERS_CREATE) ||
      user?.role?.fullAccess
    )
  }, [user])

  useEffect(() => {
    if (user && !canCreateMembers) {
      navigate('/access-denied')
    }
  }, [canCreateMembers, navigate, user])

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty }
  } = useForm<CreateCompanyUserBody>({
    defaultValues: {
      email: '',
      relation: '' as CompanyUsersRelation,
      jobTitle: '',
      roleId: '',
      workspaces: []
    },
    resolver: standardSchemaResolver(createCompanyUserBodySchema),
    mode: 'onBlur'
  })

  const isEmailFormatError = errors.email?.message === 'email.invalid_format'
  const isEmailDomainError =
    errors.email?.message === 'common.email.invalid_domain'

  const onSubmit = (data: CreateCompanyUserBody) => {
    createCompanyUserMutation.mutate(data, {
      onSuccess: () => {
        showSnackbar({
          message: t('create_invitation_view.success'),
          severity: 'success'
        })
        navigate(`/companies/${companyId}/invitations/sent`)
      },
      onError: (error: any) => {
        if (
          error.status === 409 &&
          error.response.data.data.reason ===
            ErrorReasons.PENDING_USER_INVITATION
        ) {
          NiceModal.show(ResendInvitationModal, {
            email: data.email,
            companyId
          })
          return
        }

        if (
          error.status === 409 &&
          error.response.data.data.reason ===
            ErrorReasons.USER_ALREADY_IN_COMPANY
        ) {
          showSnackbar({
            message: t('create_invitation_view.user_already_exists'),
            severity: 'error'
          })
          return
        }

        showSnackbar({
          message: t('create_invitation_view.error'),
          severity: 'error'
        })
      }
    })
  }

  if (
    isCompanyUserLoading ||
    isRolesLoading ||
    isWorkspacesLoading ||
    isCompanyLoading
  ) {
    return (
      <Stack direction='column' spacing='1rem'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Skeleton variant='text' width='6rem' height={40} />
          <Skeleton variant='text' width='6rem' height={40} />
        </Stack>

        <Stack direction='column' spacing='2rem'>
          <Stack direction='column' spacing='.5rem'>
            <Skeleton variant='text' width='20rem' height={50} />
            <Skeleton variant='text' width='30rem' height={40} />
          </Stack>

          <Stack direction='column' spacing='1.5rem'>
            <Skeleton variant='rectangular' width='36rem' height={56} />
            <Skeleton variant='rectangular' width='36rem' height={56} />
            <Skeleton variant='rectangular' width='36rem' height={56} />
          </Stack>

          <Skeleton variant='rectangular' width='8rem' height={40} />
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <SubHeaderProfile
        title={company?.name || ''}
        subtitle={t('create_invitation_view.tab')}
        isDirty={false}
      />

      <Container maxWidth={false} sx={{ maxWidth: '61.5rem' }}>
        <Stack direction='column' spacing='2rem' sx={{ marginY: '3rem' }}>
          <Typography
            variant='h5'
            sx={{ fontWeight: 'bold', padding: '1rem 1.25rem' }}
          >
            {t('create_invitation_view.title')}
          </Typography>

          <Stack direction='column' spacing='2rem'>
            <Paper elevation={1} sx={paperStyles}>
              <Stack direction='column' spacing='1.5rem'>
                <Stack direction='column' spacing='.5rem'>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h6'>
                      {t('create_invitation_view.user_data.title')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('create_company.required')}
                    </Typography>
                  </Stack>
                  <Typography variant='body1'>
                    {t('create_invitation_view.user_data.subtitle')}
                  </Typography>
                </Stack>

                <Stack direction='column' spacing='1.5rem'>
                  <TextField
                    {...register('email')}
                    label={`${t('create_invitation_view.user_data.email')}`}
                    error={!!errors.email}
                    fullWidth
                    required
                    variant='filled'
                  />

                  <Controller
                    name='relation'
                    control={control}
                    render={({ field }) => (
                      <FormControl variant='filled' fullWidth>
                        <InputLabel>
                          {t('create_invitation_view.user_data.relation')}
                        </InputLabel>
                        <Select
                          {...field}
                          displayEmpty
                          required
                          sx={{
                            background: 'rgba(255, 255, 255, 0.09)',
                            borderRadius: '0.25rem 0.25rem 0 0'
                          }}
                        >
                          {Object.values(CompanyUsersRelation).map(relation => (
                            <MenuItem key={relation} value={relation}>
                              {t(`relation.${relation}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <TextField
                    {...register('jobTitle')}
                    label={t('create_invitation_view.user_data.job_title')}
                    error={!!errors.jobTitle}
                    helperText={
                      errors.jobTitle && t(errors.jobTitle.message as string)
                    }
                    fullWidth
                    variant='filled'
                  />

                  {isEmailFormatError && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: '0.25rem',
                        background: '#EF9A9A',
                        color: 'rgba(0, 0, 0, 0.87)'
                      }}
                    >
                      {t('create_invitation_view.email_format_warning')}
                    </Alert>
                  )}

                  {isEmailDomainError && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: '0.25rem',
                        background: '#EF9A9A',
                        color: 'rgba(0, 0, 0, 0.87)'
                      }}
                    >
                      {t('create_invitation_view.email_corporate_warning')}
                    </Alert>
                  )}
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={1} sx={paperStyles}>
              <Stack direction='column' spacing='1.5rem'>
                <Stack direction='column' spacing='.5rem'>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                      {t('create_invitation_view.projects_and_role.title')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('create_company.required')}
                    </Typography>
                  </Stack>
                  <Typography variant='body1'>
                    {t('create_invitation_view.projects_and_role.subtitle')}
                  </Typography>
                </Stack>

                <Stack direction='column' spacing='1.5rem'>
                  <Controller
                    name='roleId'
                    control={control}
                    render={({ field }) => (
                      <FormControl variant='filled' fullWidth>
                        <InputLabel>
                          {t('create_invitation_view.projects_and_role.role')}
                        </InputLabel>
                        <Select
                          {...field}
                          displayEmpty
                          required
                          fullWidth
                          sx={{
                            background: 'rgba(255, 255, 255, 0.09)',
                            borderRadius: '0.25rem 0.25rem 0 0'
                          }}
                          renderValue={selected => {
                            const selectedRole = roles?.find(
                              role => role.id === selected
                            )

                            if (!selectedRole) return selected

                            return (
                              <Typography sx={{ textTransform: 'capitalize' }}>
                                {isDefaultRole(selectedRole.name)
                                  ? t(`role.${selectedRole.name}`)
                                  : selectedRole.name}
                              </Typography>
                            )
                          }}
                        >
                          {(roles || []).map(role => (
                            <MenuItem
                              key={role.id}
                              value={role.id}
                              sx={{
                                textTransform: 'capitalize'
                              }}
                            >
                              {isDefaultRole(role.name)
                                ? t(`role.${role.name}`)
                                : role.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name='workspaces'
                    control={control}
                    render={({ field }) => {
                      const selectedIds = field.value || []
                      const allWorkspaceIds =
                        workspaces?.map(workspace => workspace.id) || []
                      const allSelected =
                        selectedIds.length === allWorkspaceIds.length

                      const handleChange = (event: any) => {
                        const value = event.target.value

                        if (value[value.length - 1] === '__SELECT_ALL__') {
                          const newValue = allSelected ? [] : allWorkspaceIds
                          field.onChange(newValue)
                        } else {
                          field.onChange(value)
                        }
                      }

                      return (
                        <Select
                          {...field}
                          multiple
                          displayEmpty
                          onChange={handleChange}
                          value={field.value}
                          fullWidth
                          sx={{
                            background: 'rgba(255, 255, 255, 0.09)',
                            borderRadius: '0.25rem 0.25rem 0 0'
                          }}
                          renderValue={selected => {
                            if (selected.length === 0) {
                              return (
                                <Typography color='text.secondary'>
                                  {t(
                                    'create_invitation_view.projects_and_role.choose_projects'
                                  )}
                                </Typography>
                              )
                            }

                            const selectedNames =
                              workspaces
                                ?.filter(workspace =>
                                  selected.includes(workspace.id)
                                )
                                .map(workspace => workspace.name) || []

                            return selectedNames.join(', ')
                          }}
                        >
                          <MenuItem value='__SELECT_ALL__'>
                            <Checkbox checked={allSelected} />
                            <ListItemText
                              primary={t(
                                'create_invitation_view.projects_and_role.all_projects'
                              )}
                            />
                          </MenuItem>

                          {(workspaces || []).map(workspace => (
                            <MenuItem key={workspace.id} value={workspace.id}>
                              <Checkbox
                                checked={selectedIds.includes(workspace.id)}
                              />
                              <ListItemText primary={workspace.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      )
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ paddingTop: '1rem' }}
            >
              <Button
                variant='text'
                onClick={() => navigate(-1)}
                sx={{ color: 'text.primary', textTransform: 'none' }}
              >
                {t('common.cancel')}
              </Button>

              <LoaderButton
                variant='contained'
                disabled={
                  createCompanyUserMutation.isPending || !isValid || !isDirty
                }
                loading={createCompanyUserMutation.isPending}
                onClick={handleSubmit(onSubmit)}
                label={t('create_invitation_view.button')}
                sx={{
                  textTransform: 'none',
                  width: '14.5rem',
                  color: '#171B1E'
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </>
  )
}
